import { useEffect, useState, useCallback, useRef } from 'react';
import graphqlService from '../services/graphql';

// 原生防抖函数
function debounce(func, delay) {
  let timeoutId;
  return function (...args) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(this, args), delay);
  };
}

export function useSearchData() {
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showSkeleton, setShowSkeleton] = useState(false); // 新增骨架屏显示状态
  const [equipments, setEquipments] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  
  // 使用 useRef 来存储 offset，避免依赖项问题
  const offsetRef = useRef(0);
  
  // 使用 useRef 来存储 searchText，避免函数重新创建
  const searchTextRef = useRef('');
  
  // 搜索状态
  const [searchText, setSearchText] = useState('');
  
  // 同步 searchText 到 ref
  useEffect(() => {
    searchTextRef.current = searchText;
  }, [searchText]);

  const LIMIT = 30; // 每页加载数量

  // 多字段搜索数据加载
  const loadSearchData = useCallback(async (isLoadMore = false) => {
    const currentSearchText = searchTextRef.current;

    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
      offsetRef.current = 0;
      
      // 只在首次搜索且没有数据时设置骨架屏延迟显示
      let skeletonTimer = null;
      if (equipments.length === 0) {
        skeletonTimer = setTimeout(() => {
          setShowSkeleton(true);
        }, 300); // 300ms后才显示骨架屏
      }
    }

    try {
      const currentOffset = isLoadMore ? offsetRef.current : 0;
      let data;
      
      if (!currentSearchText.trim()) {
        // 当搜索文本为空时，显示所有装备
        data = await graphqlService.searchEquipments({}, currentOffset, LIMIT);
      } else {
        // 使用多字段搜索方法（搜索模式专用）
        // 这个方法会在后端搜索 name, brandName, color, model, gender, style, flex 等字段
        data = await graphqlService.multiFieldSearchEquipments(
          currentSearchText, 
          currentOffset, 
          LIMIT
        );
      }
      
      // 隐藏骨架屏
      setShowSkeleton(false);
      
      if (isLoadMore) {
        // 加载更多：追加到现有数据
        setEquipments(prev => [...prev, ...(data || [])]);
        offsetRef.current += LIMIT;
      } else {
        // 新搜索：替换现有数据
        setEquipments(data || []);
        offsetRef.current = LIMIT;
      }
      
      // 判断是否还有更多数据
      setHasMore((data || []).length === LIMIT);
      
    } catch (error) {
      console.error('搜索失败:', error);
      setShowSkeleton(false); // 出错时也要隐藏骨架屏
      if (!isLoadMore) {
        setEquipments([]);
      }
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [equipments.length]); // 添加equipments.length作为依赖项

  // 加载更多数据
  const loadMore = useCallback(() => {
    if (!loadingMore && hasMore) {
      loadSearchData(true);
    }
  }, [loadSearchData, loadingMore, hasMore]);

  // 防抖搜索函数 - 使用 useRef 来避免依赖项问题
  const debouncedSearchRef = useRef();
  
  useEffect(() => {
    // 创建防抖函数，只在组件挂载时创建一次
    if (!debouncedSearchRef.current) {
      debouncedSearchRef.current = debounce(() => {
        loadSearchData(false);
      }, 300);
    }
  }, [loadSearchData]);

  // 当搜索文本改变时，重新搜索
  useEffect(() => {
    if (debouncedSearchRef.current) {
      debouncedSearchRef.current();
    }
  }, [searchText]);

  return {
    // 数据状态
    loading,
    loadingMore,
    showSkeleton, // 新增骨架屏显示状态
    equipments,
    hasMore,
    
    // 搜索状态
    searchText,
    setSearchText,
    
    // 操作方法
    loadMore
  };
}